/**
 * The printable order summary, laid out from the server's print model: only
 * the fields the order sharing configuration switches on for print (or for
 * the invoice copy), under their display names, with the Retailer Connect
 * header and footer. Registered outside the shell at /print/field-order/:id.
 * "Download PDF" fetches the same summary rendered on the server
 * (GET /orders/:id/pdf), for sharing as a file.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileDown, Printer } from 'lucide-react';
import { Button, EmptyState, Spinner } from '@/modules/accounting/components/ui';
import { api } from '@/modules/accounting/lib/api';
import { reportError } from '@/modules/accounting/lib/store';
import { CM, fmtDate, useFetch, type Row } from './common';
import { Select } from '@/components/ui/select';

const DATE_KEYS = new Set(['order_date']);

export function FieldOrderPrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [output, setOutput] = useState<'print' | 'invoice'>('print');
  const { data, error, loading } = useFetch<Row>(CM(`/orders/${id}/print`), { output });
  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await api.download(CM(`/orders/${id}/pdf`), undefined, `Order-${data?.order_no ?? id}.pdf`);
    } catch (err) {
      reportError(err);
    } finally {
      setDownloading(false);
    }
  };

  const bar = (
    <div className="no-print sticky top-0 z-10 bg-white dark:bg-ink-900 border-b border-ink-200 dark:border-ink-800 px-4 py-2.5 flex items-center gap-2 flex-wrap">
      <button type="button" onClick={() => navigate(-1)} className="btn-ghost btn-icon btn-sm" aria-label="Back"><ArrowLeft size={16} /></button>
      <span className="text-[15px] font-medium">Order summary {data?.order_no ?? ''}</span>
      <div className="flex-1" />
      <Select compact value={output} onChange={(e) => setOutput(e.target.value as 'print' | 'invoice')}>
        <option value="print">Order summary</option>
        <option value="invoice">Invoice copy</option>
      </Select>
      {output === 'print' && <Button size="sm" icon={FileDown} disabled={!data} loading={downloading} onClick={downloadPdf}>Download PDF</Button>}
      <Button size="sm" variant="primary" icon={Printer} disabled={!data} onClick={() => window.print()}>Print</Button>
    </div>
  );

  if (loading && !data) return <div>{bar}<Spinner /></div>;
  if (error || !data) {
    return (
      <div>{bar}
        <div className="page">
          <EmptyState title="Nothing to print" message={error?.status === 403 ? 'You do not have permission to print field orders.' : error?.message ?? 'The order could not be loaded.'} />
        </div>
      </div>
    );
  }
  const value = (f: Row) => (f.value === null || f.value === undefined || f.value === '' ? '—' : DATE_KEYS.has(f.key) ? fmtDate(f.value) : String(f.value));
  const section = (key: string) => data.sections.find((s: Row) => s.key === key);
  const partyBlock = (key: string) => {
    const s = section(key);
    if (!s) return null;
    return (
      <div className="p-2 border border-black">
        <p className="text-[13px] text-gray-600 font-semibold">{s.title}</p>
        {s.fields.map((f: Row) => <p key={f.key} className="text-[13px] leading-snug"><span className="text-gray-600">{f.label}: </span><span className="font-medium">{value(f)}</span></p>)}
      </div>
    );
  };
  const summaryTitle = section('ORDER')?.fields.find((f: Row) => f.key === 'order_summary');
  const rest = ['USER', 'PAYABLE', 'REMARKS'];

  return (
    <div className="bg-ink-200 dark:bg-ink-950 min-h-screen">
      {bar}
      <div className="py-6 flex justify-center">
        <div className="print-page bg-white text-black shadow-float" style={{ width: '210mm', minHeight: '297mm', padding: '12mm' }}>
          {data.header && <p className="text-center text-[14px] mb-2">{data.header}</p>}
          <div className="text-center border-b-2 border-black pb-2 mb-3">
            {section('COMPANY')?.fields.map((f: Row) => (
              <p key={f.key} className={f.key === 'company_name' ? 'text-[16px] font-bold' : 'text-[13px]'}>{value(f)}</p>
            ))}
            <h1 className="text-[15px] font-bold mt-1">{output === 'invoice' ? 'Invoice copy' : summaryTitle ? value(summaryTitle) : 'Order summary'}</h1>
            <p className="text-[13px]">
              {section('ORDER')?.fields.filter((f: Row) => f.key !== 'order_summary').map((f: Row) => `${f.label}: ${value(f)}`).join('   ·   ')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {partyBlock('DISTRIBUTOR')}
            {partyBlock('RETAILER')}
          </div>
          <table className="w-full text-[13px] border-collapse mb-3">
            <thead>
              <tr>{data.grid.columns.map((c: Row) => <th key={c.key} className="border border-black px-1.5 py-1 text-left bg-gray-100">{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {data.grid.rows.map((r: Row, i: number) => (
                <tr key={i}>{data.grid.columns.map((c: Row) => <td key={c.key} className="border border-black px-1.5 py-1">{r[c.key] ?? '—'}</td>)}</tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-3 gap-2">
            {rest.map((key) => {
              const s = section(key);
              if (!s) return null;
              return (
                <div key={key} className="border border-black p-2">
                  <p className="text-[13px] text-gray-600 font-semibold">{s.title}</p>
                  {s.fields.map((f: Row) => (
                    <p key={f.key} className="text-[13px] flex justify-between gap-2">
                      <span className="text-gray-600">{f.label}</span>
                      <span className="font-medium">{f.key === 'customer_signature' ? <span className="inline-block w-28 border-b border-black">&nbsp;</span> : value(f)}</span>
                    </p>
                  ))}
                </div>
              );
            })}
          </div>
          {data.footer && <p className="text-center text-[13px] mt-4">{data.footer}</p>}
        </div>
      </div>
    </div>
  );
}
